import { HotspotButton } from '@/components/Hotspot'
import { useChapter } from '@/context/ChapterContext'
import hotspotData from '@/data/hotspot'
import type Hotspot from '@/types/Hotspot'
import { useEffect, useRef, useState } from 'react'

// Custom event to communicate hotspot state outside Three.js
export const HOTSPOT_STATE_EVENT = 'hotspot-state-change'

export type HotspotStateEvent = CustomEvent<{
  hotspot: Hotspot | undefined
  isClosing: boolean
  onClose: () => void
} | null>

export default function HotspotController() {
  const { showHotspots } = useChapter()
  const { currentChapterNumber: currentChapter } = useChapter()
  const [selectedHotspot, setSelectedHotspot] = useState<Hotspot | undefined>(undefined)
  const [isClosing, setIsClosing] = useState(false)

  // Animation duration in ms - should match the CSS duration
  const animationDuration = 500
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  // Simple function to handle opening a hotspot
  const handleHotspotClick = (hotspot: Hotspot) => {
    // If we're not in a closing animation and a different hotspot is selected
    if (!isClosing) {
      if (selectedHotspot) {
        // If we already have a hotspot open, first close it
        setIsClosing(true)

        // Store a reference to the hotspot we want to open next
        const nextHotspot = hotspot

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }

        // After animation completes, show the new hotspot
        timeoutRef.current = setTimeout(() => {
          setIsClosing(false)
          setSelectedHotspot(nextHotspot)
          timeoutRef.current = null
        }, animationDuration)
      } else {
        // If no hotspot is open, just open this one
        setSelectedHotspot(hotspot)
      }
    }
  }

  // Handle closing a hotspot
  const handleClose = () => {
    if (!isClosing) {
      setIsClosing(true)

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      // After animation completes, clear the selected hotspot
      timeoutRef.current = setTimeout(() => {
        setIsClosing(false)
        setSelectedHotspot(undefined)
        timeoutRef.current = null
      }, animationDuration)
    }
  }

  // Dispatch custom event when hotspot state changes
  useEffect(() => {
    const event = new CustomEvent(HOTSPOT_STATE_EVENT, {
      detail: selectedHotspot
        ? {
            hotspot: selectedHotspot,
            isClosing,
            onClose: handleClose,
          }
        : null,
    })
    window.dispatchEvent(event)
  }, [selectedHotspot, isClosing])

  // Don't render anything if hotspots are disabled
  if (!showHotspots) {
    return null
  }

  return (
    <>
      {hotspotData
        .filter((h) => currentChapter === h.chapter)
        .map((hotspot) => (
          <HotspotButton key={hotspot.id} data={hotspot} onClick={() => handleHotspotClick(hotspot)} />
        ))}
    </>
  )
}
